import type {
    Page,
    Product
} from './';
import type {
    CustomPagination
} from 'x1-base/types';
import type { ContentBlockConfigType, ContentBlockType } from './content-blocks';
import type { DisplayBreakpoint } from 'vuetify';

/* General Content */
export type CssStyleObject = Record<string, string>;

export interface ContentResourceLocation {
    url: string | Record<string, string>;
    isCdn?: boolean;
}

/*
    CMS Content
*/

export interface CmsResponseDetails {
    detail: string;
}

/*
    Content Blocks
*/
export type CssUnsetValue = 'unset';

export type ThemeType = 'light' | 'dark';

export type ViewportType = 'desktop' | 'mobile';

export type BreakpointType = DisplayBreakpoint;

export type BreakpointConditionType =
    'xs'
    | 'sm'
    | 'md'
    | 'lg'
    | 'xl'
    | 'xxl'
    | 'smAndUp'
    | 'mdAndUp'
    | 'lgAndUp'
    | 'xlAndUp'
    | 'smAndDown'
    | 'mdAndDown'
    | 'lgAndDown'
    | 'xlAndDown'
    | CssUnsetValue;

export type CssTextAlignmentValues =
    'start'
    | 'end'
    | 'center'
    | 'left'
    | 'right'
    | 'justify'
    | 'justify-all'
    | CssUnsetValue;

export type FlexboxClassAlignmentValues =
    'start'
    | 'end'
    | 'center'
    | 'space-between'
    | 'space-around'
    | 'stretch'
    | 'auto'
    // Self
    | 'self-start'
    | 'self-end'
    | 'self-center'
    | 'self-space-between'
    | 'self-space-around'
    | 'self-stretch'
    | 'self-auto'
    // Content
    | 'content-start'
    | 'content-end'
    | 'content-center'
    | 'content-space-between'
    | 'content-space-around'
    | 'content-stretch'
    | 'content-auto'
    | CssUnsetValue;

export type ThemeItem<T> = Record<ThemeType, T>;

export interface ContentInlineVideoProps {
    autoplay?: Nullable<boolean>;
    controls?: Nullable<boolean>;
    loop?: Nullable<boolean>;
    muted?: Nullable<boolean>;
    poster?: Nullable<string>;
    height?: Nullable<number | string>;
    width?: Nullable<number | string>;
}

export interface ContentInlineVideoSource {
    src: string;
    type: string;
}

export interface ContentVideoSource {
    source: ContentInlineVideoSource[];
}

export interface ContentImageSrcSet {
    url: string;
    size: string;
    isCdn?: boolean;
}
export interface ContentImage {
    srcset: ContentImageSrcSet[];
}

export interface ContentBlockDividerLine {
    top?: Nullable<boolean>;
    bottom?: Nullable<boolean>;
    start?: Nullable<boolean>;
    end?: Nullable<boolean>;
}

export interface ContentBackgroundImage extends ContentResourceLocation {}

export interface ContentBlockBackground {
    backgroundImage?: ThemeItem<ContentBackgroundImage>;
    backgroundAttachment?: string;
    backgroundClip?: string;
    backgroundColor?: string;
    backgroundOrigin?: string;
    backgroundPosition?: string;
    backgroundRepeat?: string;
    backgroundSize?: string;
}

export interface ContentBlockBackgroundConfig {
    base: ContentBlockBackground;
    breakpoint?: PartialRecord<BreakpointType, Partial<ContentBlockBackground>>;
}

export interface CssColorAttrs {
    color: string | CssUnsetValue;
    background: string | CssUnsetValue;
}

export class ContentBlockThemeStyleItems {
    cssClass: string[] = [];
    cssStyle: Record<string, string> = {};
}

export type AppearanceBreakpoint = PartialRecord<BreakpointType, ContentBlockThemeStyleItems>;

export interface ContentBlockAppearanceItem {
    base: ContentBlockThemeStyleItems;
    theme: ThemeItem<ContentBlockThemeStyleItems>;
    breakpoint: AppearanceBreakpoint;
}

export interface ContentBlockAppearance {
    appearance: ContentBlockAppearanceItem;
}

export interface RowConfig extends Partial<ContentBlockThemeStyleItems>, Partial<ContentBlockAppearance> {
    align?: Nullable<string>;
    dense?: Nullable<boolean>;
    noGutters?: Nullable<boolean>;
    alignContent?: Nullable<string>;
    alignContentSm?: Nullable<string>;
    alignContentMd?: Nullable<string>;
    alignContentLg?: Nullable<string>;
    alignContentXl?: Nullable<string>;
    alignSm?: Nullable<string>;
    alignMd?: Nullable<string>;
    alignLg?: Nullable<string>;
    alignXl?: Nullable<string>;
    justify?: Nullable<string>;
    justifySm?: Nullable<string>;
    justifyMd?: Nullable<string>;
    justifyLg?: Nullable<string>;
    justifyXl?: Nullable<string>;
}

export interface BlockType<T = ContentBlockConfigType> extends ContentBlockAppearance {
    type: ContentBlockType;
    config: T;
}

export interface ContentLink {
    content?: Nullable<string>;
    altText?: string;
    path?: string;
    url?: string;
    target?: string;
    youtubeId?: string;
}

export interface ContentBlock extends ContentBlockAppearance {
    id: number | string;
    constrainedWidth: boolean;
    fillHeight?: boolean;
    hyperlinkBlock: Nullable<ContentLink>;
    dividerLine: ContentBlockDividerLine;
    background?: ContentBlockBackgroundConfig;
    row: RowConfig;
    blocks: BlockType[];
}

export interface ColorOption {
    code: string | string[];
    value: string;
    text: string;
    available: boolean;
}

export interface ColorOptionGroup {
    productName: string;
    qty: number;
    colors: ColorOption[];
}

// Dynamic Content: Metadata Objects

export type ContentData = Product | Page | CustomPagination<Product>;

export type ContentMetaType = 'page' | 'collection' | 'article' | 'product' | 'market' | 'category';

export type BannerMediaType = 'image' | 'video';

export interface BannerVariant {
    type: BannerMediaType;
    url: string;
    mime: string;
    width: Nilable<number>;
    height: Nilable<number>;
    textTheme: Nilable<'light' | 'dark'>;
    poster?: Nilable<string>;
}

export interface Banner {
    alt: Nilable<string>;
    desktop: Nilable<BannerVariant>;
    mobile: Nilable<BannerVariant>;
}

export interface ContentMeta {
    uid?: string;
    path: string;
    title: string;
    type: ContentMetaType;
    seoTitle: Nullable<string>;
    seoDescription: Nullable<string>;
    seoKeywords: Nullable<string>;
    banner: Nilable<Banner>;
}

export interface ContentMetaRedirect {
    type: 'redirect';
    path: string;
}

export interface ContentMetaResponse {
    meta: ContentMeta | ContentMetaRedirect;
    content: Nullable<ContentData>;
}

export interface CompanyPartners {
    name: string;
    label: string;
    url: string;
    logoSolid: string;
    logoColor: string;
    width: number;
    height: number;
}

// TEMP ITEMS FOR STATIC CONTENT
export interface CarouselListButtonItem {
    type: string;
    label: string;
    url: string;
    color: string;
}

export interface CarouselListImage {
    src: string;
    contain: boolean;
    path: Nullable<string>;
    altText: string;
    height: string;
    width: string;
}

export interface CarouselList {
    title: string;
    titleLogoPath: Nullable<string>;
    subTitle: string;
    image: CarouselListImage;
    buttonLink: CarouselListButtonItem[];
}

export interface LegacyContentItemLink {
    productId: string;
    text: string;
    externalUrl?: string;
}

export interface LegacyContentItemUrl {
    url: string;
    text: string;
}

export interface LegacyContentVideoStaticItem {
    desktop: Nullable<string>;
    mobile: Nullable<string>;
}

// Legacy Content
export interface LegacyContentItem {
    id: number;
    title: string;
    text: string;
    shortText: Nullable<string>;
    video: Nullable<string>;
    hideFromCatalog: boolean;
    image: Record<string, string>;
    altText: string;
    backgroundColor: string;
    textColor: string;
    height: string;
    width: string;
    textAlign: string;
    textPosX: string;
    textOffsetX: number;
    textPosY: string;
    textOffsetY: number;
    imageHeight: number;
    imagePosX: string;
    imageOffsetX: string;
    imagePosY: string;
    imageOffsetY: number;
    layoutOverride?: Partial<ContentBlock>;
    layoutType?: string;
    links?: Nullable<LegacyContentItemLink[]>;
    urls?: Nullable<LegacyContentItemUrl[]>;
    videoStatic?: LegacyContentVideoStaticItem;
}

export type ContentComponentType = 'TypePage' | 'TypeCollection' | 'TypeArticle' | 'TypeProduct' | 'None';
export type ContentSubBlockComponentType = 'BlockTypeText' | 'BlockTypeImageStatic' | 'BlockTypeVideoStatic' | 'BlockTypeInlineVideo';

export interface GetContentDataResponse extends ContentMetaResponse {
    baseComponent: ContentComponentType;
}
